import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { SERVER_TIMEZONE, trackerSteps } from '../constants';
dayjs.extend(utc);
dayjs.extend(timezone);
/**
 * Function that formats date to specified `formatString`
 * and to declared timezone
 *
 * @param {string} dateString
 * @param {string} formatString
 * @returns {string} formatted date
 *
 * @example
 * const eventDateTime = '2020-09-02T10:58:29.486Z'
 * formattedDate(eventDateTime, 'MMM D') => 'Sep 02'
 */
export function formattedDate(dateString, formatString) {
    return dayjs(dateString).tz(SERVER_TIMEZONE).format(formatString);
}
/**
 *  Function to mask account based on UI standards
 *
 * @param {string} name
 * @param {number} number
 * @returns {string} formatted account info
 *
 * @example
 * const name = 'Savings Account'
 * const number = '123456789'
 * maskAccount(name, number) => 'Savings Account••6789'
 */
export function maskAccount(name, number) {
    return `${name} ••${number.slice(-4)}`;
}
/**
 * Function to add space between characters
 * Used for a11y so numbers are read correctly
 *
 * @param {string} value
 * @returns {string} formatted value
 *
 */
export function spreadNumberA11y(value) {
    return value.split('').join(' ');
}
/**
 *  Function to get timeline step status
 *
 * @param {string} activityName
 * @param {string} currentStatus
 * @returns {string} status
 *
 */
export function getStatus({ activityName, currentStatus, }) {
    if (activityName !== 'ACHTransfer' && currentStatus === 'InProcess') {
        return 'Ordered';
    }
    if (activityName === 'DebitCardOrder' && currentStatus === 'Delayed') {
        return 'Delivery delayed';
    }
    if (activityName === 'DebitCardOrder' && currentStatus === 'DeliveryFailed') {
        return 'Delivery unsuccessful';
    }
    if (currentStatus === 'Confirmed') {
        return 'Ordered';
    }
    return currentStatus;
}
/**
 *  Function to get timeline marker type
 *
 * @param {StatusTrackerActivity<EventType>} event
 * @param {string} step
 * @returns {string} marker type
 *
 */
export function getMarkerType({ event, step, }) {
    const steps = trackerSteps[event.activityName];
    if (steps[0] === step) {
        return 'start';
    }
    if (steps[steps.length - 1] === step ||
        step === 'Canceled' ||
        step === 'Delivery unsuccessful') {
        return 'end';
    }
    return 'default';
}
/**
 *  Function to create debit card account name/number
 *
 * @param {string} accountType
 * @param {number} number
 * @returns {string} formatted account info
 *
 * @example
 * const accountType = 'DDA'
 * const number = '1234'
 * getDebitCardAccountDisplay(name, number) => 'Spending Account ••1234'
 */
export function getDebitCardAccountDisplay(accountType, number) {
    switch (accountType) {
        case 'DDA':
            return `Spending Account ••${number}`;
        case 'SDA':
            return `Online Savings ••${number}`;
        case 'MMA':
            return `Money Market Account ••${number}`;
        case 'CMG':
            return `CMG Savings Account ••${number}`;
        default:
            return `••${number}`; // If accountType is incorrect just show last 4 numbers
    }
}
/**
 *  Function to get grammatically correct message in relation to withdrawal day and current day
 *
 * @param {string} withdrawalDate
 * @returns {string} withdrawal message
 */
export function getWithdrawalMessage(withdrawalDate) {
    const formattedWithdrawalDate = formattedDate(withdrawalDate, 'MMM D, YYYY');
    const isAfterWithdrawalDate = dayjs()
        .tz(SERVER_TIMEZONE)
        .isAfter(dayjs(withdrawalDate).tz(SERVER_TIMEZONE), 'day'); // compares calendar days without regard for time
    if (isAfterWithdrawalDate) {
        return `We withdrew funds on ${formattedWithdrawalDate}`;
    }
    return `We'll withdraw funds on ${formattedWithdrawalDate}`;
}
//# sourceMappingURL=utils.js.map